import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL =
"https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/0t2i84sr_Logo.png";

const NAV_LINKS = [
{ href: "#startpakker", label: "Templates", type: "anchor" },
{ href: "#rapporter", label: "Rapporter", type: "anchor" },
{ href: "#testimonials", label: "Kunder", type: "anchor" },
{ href: "#priser", label: "Priser", type: "anchor" },
{ href: "#about", label: "Om mig", type: "anchor" }];


const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // On non-home routes, always render with the white "scrolled" style
  // so the nav is readable on white/slate backgrounds.
  const showSolid = isScrolled || !isHome;

  // Anchor links from non-home routes must navigate back to home + scroll
  const anchorTarget = (hash) => isHome ? hash : `/${hash}`;
  const ctaTarget = isHome ? "#booking" : "/#booking";
  const logoTarget = isHome ? "#hero" : "/";

  const desktopLinkClass = `font-inter font-medium text-sm transition-colors whitespace-nowrap ${
  showSolid ? "text-slate-600 hover:text-teal-600" : "text-slate-300 hover:text-white"}`;

  const mobileLinkClass = "block py-3 px-4 text-slate-700 hover:text-teal-600 font-medium";

  const renderLink = (link, className, onClick) => {
    const testId = `nav-${link.href.replace(/[#/]/g, "")}`;
    if (link.type === "route") {
      return (
        <Link key={link.href} to={link.href} className={className} data-testid={testId} onClick={onClick}>
          {link.label}
        </Link>);

    }
    const target = anchorTarget(link.href);
    return isHome ?
    <a key={link.href} href={target} className={className} data-testid={testId} onClick={onClick}>
        {link.label}
      </a> :

    <Link key={link.href} to={target} className={className} data-testid={testId} onClick={onClick}>
        {link.label}
      </Link>;

  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      showSolid ? "bg-white/95 backdrop-blur-md nav-shadow" : "bg-transparent"}`
      }
      data-testid="main-navigation">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {isHome ?
          <a href={logoTarget} className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="Valubi" className="h-10 w-auto" />
            </a> :

          <Link to={logoTarget} className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="Valubi" className="h-10 w-auto" />
            </Link>
          }

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => renderLink(link, desktopLinkClass))}
            {isHome ?
            <a
              href={ctaTarget}
              className="btn-primary font-inter font-medium text-sm px-5 py-2.5 rounded-lg whitespace-nowrap"
              data-testid="nav-cta-button">

                Book et møde
              </a> :

            <Link
              to={ctaTarget}
              className="btn-primary font-inter font-medium text-sm px-5 py-2.5 rounded-lg whitespace-nowrap"
              data-testid="nav-cta-button">

                Book et møde
              </Link>
            }
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle">

            {isOpen ?
            <X className={showSolid ? "text-slate-900" : "text-white"} size={24} /> :

            <Menu className={showSolid ? "text-slate-900" : "text-white"} size={24} />
            }
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen &&
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="md:hidden mobile-menu absolute top-full left-0 right-0 py-4 px-4"
        data-testid="mobile-menu">

          {NAV_LINKS.map((link) =>
        renderLink(link, mobileLinkClass, () => setIsOpen(false))
        )}
          {isHome ?
        <a
          href={ctaTarget}
          className="block mt-4 text-center btn-primary py-3 px-4 rounded-lg font-medium"
          onClick={() => setIsOpen(false)}>

              Book et møde
            </a> :

        <Link
          to={ctaTarget}
          className="block mt-4 text-center btn-primary py-3 px-4 rounded-lg font-medium"
          onClick={() => setIsOpen(false)}>

              Book et møde
            </Link>
        }
        </motion.div>
      }
    </nav>);

};

export default Navigation;
